import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Badge,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Photo as PhotoIcon
} from '@mui/icons-material';
import { useAuth } from 'shared/contexts/AuthContext';
import { useContractorFiles } from 'shared/hooks/useContractorFiles';
import { AvatarEditModal } from './AvatarEditModal';
import { getRoleDisplayName } from 'shared/utils/roleUtils';

export const SecurityProfile: React.FC = () => {
  const { user, userAvatarUrl } = useAuth();
  const { loading } = useContractorFiles();
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);

  return (
    <Box>
      {/* Validadores Ops Identity Section */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'center', sm: 'flex-start' },
                mb: 3,
                gap: 2
              }}>
                <Tooltip title={userAvatarUrl ? "Clic para editar foto" : "Agregar foto de perfil"}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <IconButton
                        sx={{
                          bgcolor: 'success.main',
                          color: 'white',
                          width: 28,
                          height: 28,
                          '&:hover': {
                            bgcolor: 'success.dark',
                          }
                        }}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAvatarModalOpen(true);
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    }
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        '&:hover': {
                          '& .MuiAvatar-root': {
                            opacity: 0.8
                          }
                        }
                      }}
                      onClick={() => setAvatarModalOpen(true)}
                    >
                      <Avatar
                        sx={{
                          width: { xs: 100, sm: 80 },
                          height: { xs: 100, sm: 80 },
                          bgcolor: userAvatarUrl ? 'transparent' : 'success.main',
                          transition: 'opacity 0.2s',
                          border: !userAvatarUrl ? '2px dashed' : 'none',
                          borderColor: 'action.disabled'
                        }}
                        src={userAvatarUrl || undefined}
                      >
                        {!userAvatarUrl && (user?.firstName && user?.lastName
                          ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
                          : <PhotoIcon sx={{ fontSize: 40 }} />
                        )}
                      </Avatar>
                      {loading && (
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(255,255,255,0.8)',
                          borderRadius: '50%'
                        }}>
                          <CircularProgress size={24} />
                        </Box>
                      )}
                    </Box>
                  </Badge>
                </Tooltip>
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, width: '100%' }}>
                  <Typography variant="h6" color="success.dark" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    {user?.fullName || `${user?.firstName} ${user?.lastName}`}
                  </Typography>
                  <Chip
                    label="Seguridad"
                    color="success"
                    size="small"
                    sx={{ mb: 1, mt: 0.5 }}
                  />
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                    {user?.position || 'Seguridad'}
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    value={user?.firstName || ''}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Apellido"
                    value={user?.lastName || ''}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Nombre Completo"
                    value={user?.fullName || user?.name || ''}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={user?.email || ''}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={user?.phone || user?.profile?.phone || ''}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Cédula"
                    value={user?.cedula || ''}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Estado"
                    value={user?.isActive ? 'Activo' : 'Inactivo'}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Rol"
                    value={user?.role ? getRoleDisplayName(user.role) : ''}
                    disabled
                    size="small"
                  />
                </Grid>
                {user?.position && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Posición"
                      value={user.position}
                      disabled
                      size="small"
                    />
                  </Grid>
                )}
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="ID Usuario"
                    value={user?._id || user?.id || ''}
                    disabled
                    size="small"
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Fecha de Creación"
                    value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : ''}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Última Actualización"
                    value={user?.updatedAt ? new Date(user.updatedAt).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : ''}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Último Inicio de Sesión"
                    value={user?.lastLogin || user?.profile?.lastLogin ? new Date(user.lastLogin || user.profile?.lastLogin || '').toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'Nunca'}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Términos Aceptados"
                    value={user?.acceptedTerms ? `Sí (${new Date(user.termsAcceptedAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })})` : 'No'}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Política de Privacidad"
                    value={user?.acceptedPrivacyPolicy ? `Sí (${new Date(user.privacyPolicyAcceptedAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })})` : 'No'}
                    disabled
                    size="small"
                  />
                </Grid>
                {user?.company && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ mt: { xs: 1, sm: 2 }, mb: 1, fontWeight: 600 }}>
                        Información de Compañía
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        fullWidth
                        label="Compañía"
                        value={user.company.name || ''}
                        disabled
                        size="small"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        fullWidth
                        label="N° Documento de Identidad"
                        value={user.company.ruc || ''}
                        disabled
                        size="small"
                      />
                    </Grid>
                    {user.company.email && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Email Compañía"
                          value={user.company.email}
                          disabled
                          size="small"
                        />
                      </Grid>
                    )}
                    {user.company.phone && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Teléfono Compañía"
                          value={user.company.phone}
                          disabled
                          size="small"
                        />
                      </Grid>
                    )}
                    {user.company.address && (
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Dirección Compañía"
                          value={user.company.address}
                          disabled
                          size="small"
                        />
                      </Grid>
                    )}
                  </>
                )}
                {user?.departments && user.departments.length > 0 && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ mt: { xs: 1, sm: 2 }, mb: 1, fontWeight: 600 }}>
                        Departamentos ({user.departments.length})
                      </Typography>
                    </Grid>
                    {user.departments.map((dept, index) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={dept._id}>
                        <TextField
                          fullWidth
                          label={`Departamento ${index + 1}`}
                          value={`${dept.name}${dept.code ? ` (${dept.code})` : ''}`}
                          disabled
                          size="small"
                        />
                      </Grid>
                    ))}
                  </>
                )}
                {user?.supervisedCompanies && user.supervisedCompanies.length > 0 && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" color="textSecondary" sx={{ mt: { xs: 1, sm: 2 }, mb: 1, fontWeight: 600 }}>
                        Compañías Supervisadas ({user.supervisedCompanies.length})
                      </Typography>
                    </Grid>
                    {user.supervisedCompanies.map((company, index) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={company._id}>
                        <TextField
                          fullWidth
                          label={`Compañía ${index + 1}`}
                          value={company.name}
                          disabled
                          size="small"
                        />
                      </Grid>
                    ))}
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Avatar Edit Modal */}
      <AvatarEditModal
        open={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
        currentAvatarUrl={userAvatarUrl}
        onAvatarUpdated={() => {
          // Avatar will be refreshed automatically via AuthContext
        }}
      />
    </Box>
  );
};